/**
 * The bank reconciliation screen's arithmetic and its one safety rule.
 *
 * Task 8 of docs/bank-reconciliation-milestone-plan.md. Everything here is
 * pure: it takes the matcher's report exactly as `POST /api/bank-statements/
 * :id/matches` sends it and answers what the screen should show and what a
 * button is allowed to send.
 *
 * IN `lib/` AND NOT IN BankReconciliation.jsx, for the reason views.js,
 * routing.js, cashBookGrid.js and absaStatement.js all state at their heads:
 * `npm test -w web` is node's own runner over `src/lib/*.test.js`, and node
 * cannot parse JSX. A rule that only a component could exercise is a rule
 * nobody checks — and the rule below is the single most dangerous thing on
 * this screen.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DO
 * ---------------------------------------------------------------------------
 *
 * IT NEVER SCORES A MATCH. Decision 9 of the plan: confidence is computed
 * SERVER-side and never in the browser. `slips.js:567-581` and
 * `SlipReview.jsx:236-245` record what happened the one time a proposal was
 * re-derived client-side — "it drifted from the real matching in ways that
 * mattered", in three specific ways. So `confidence`, `band` and `basis`
 * arrive on the wire, already decided, and this module only ever COPIES them:
 * there is no threshold constant here, no scoring function, and no rule that
 * turns a number into a band. `bandPresentation` maps the server's WORD to a
 * presentation; it is given no number and cannot compute one.
 *
 * IT NEVER RE-WORDS `basis`. The sentence is written once, by the server, in
 * `proposeFromHistory`'s voice — a fact, no verdict, never the score (spec §2).
 * The screen renders it verbatim. `basis` is the whole point of the screen:
 * "it matches but we approve it" only works if the person approving can see
 * WHY, in words they can check against the statement in front of them. A
 * summary of a sentence that was written to be read is a summary of the
 * evidence.
 *
 * IT DOES ARITHMETIC ON CENTS, WITH BigInt AND NEVER A FLOAT. `centsToAmount`
 * is the only reason there is arithmetic here at all: the matcher sends
 * `amountCents` as a decimal string of integer cents (the route stringifies a
 * BigInt, because `JSON.stringify` throws on one) while `money()` in api.js
 * formats a DECIMAL string. Something has to bridge those two, and
 * `0.1 + 0.2 !== 0.3` is a curiosity in most software and a misstatement in a
 * ledger.
 */

// ===========================================================================
// THE ACKNOWLEDGEMENT KEY — the rule this file exists for
// ===========================================================================

/**
 * The acknowledgement for ONE approval, keyed on the exact combination it was
 * given for. Stolen wholesale from `lib/ackKey.js`, and the reason it is
 * stolen is worth restating, because this screen multiplies the hazard.
 *
 * `SlipReview.jsx:266-281` records the bug. The denied-VAT acknowledgement was
 * once a plain boolean reset by a `useEffect` keyed on the fields it guarded.
 * That looked equivalent and was not: React bails out of an effect whose
 * dependencies are referentially equal to last render's, so when two
 * CONSECUTIVE slips proposed the same account and tax code, the effect never
 * re-fired and — in that file's own words — *"a tick from the PREVIOUS slip
 * silently authorised the next post."*
 *
 * A RECONCILIATION SCREEN HAS THAT HAZARD MULTIPLIED, and it is not a
 * hypothetical. The plan measured the shape of it on the owner's real
 * statement: **53 rows share a `(date, amount, description)` triple, eleven
 * identical `PROOF OF PMT EMAIL` on 30 July alone.** So two consecutive rows of
 * the queue really do render identically, really do propose entries that are
 * identical to look at (spec §6.1 is the fixture: `S1`/`S2` against `J1`/`J2`,
 * every pair scoring 0.800), and a boolean reset by an effect keyed on
 * anything the reviewer can SEE — the amount, the description, the date, the
 * band — would not re-fire between them. Then a tick meant for one row
 * approves a journal it was never shown against. And where the slip screen
 * risked one wrong post at a time, this screen has a button that sends every
 * ticked row at once.
 *
 * So the acknowledgement IS the key, and there is no reset to forget to run.
 * Three parts, each of which is a different thing to have agreed to:
 *
 *  - `statementLineId` — WHICH line of the bank's own statement. This is what
 *    stops row 212's tick reaching row 213. It is the line's identity and not
 *    its content, because the plan measured that a statement line has no
 *    natural key: dedup on content silently drops real lines, and keying an
 *    authorisation on content would silently move one.
 *  - `journalLineId` — WHICH entry in the ledger it is being paired with. A
 *    re-run of the matcher that proposes a different entry for this line
 *    produces a different key, so the tick lapses rather than authorising a
 *    pairing nobody looked at.
 *  - `confidence` — the EVIDENCE the server offered, verbatim. The same
 *    pairing re-proposed at a different score is a different claim about the
 *    same two rows, and the person who ticked the first one was not shown the
 *    second. Rendered to three decimals exactly as the server stores it
 *    (`numeric(4,3)`), so a re-run that changes nothing changes no key and a
 *    reviewer's ticks survive a refresh they did not ask for.
 *
 * `null` confidence means a person named the pairing themselves — the matcher
 * never scored it — and is spelled out rather than left to stringify, so that
 * a hand-picked candidate can never key-collide with a proposal for the same
 * two rows.
 */
export const matchAckKey = (statementLineId, journalLineId, confidence) => [
  statementLineId ?? '',
  journalLineId ?? '',
  confidence === null || confidence === undefined || Number.isNaN(Number(confidence))
    ? 'by-hand'
    : Number(confidence).toFixed(3),
].join(':');

/**
 * The key for "this line, paired with that entry" as the screen would send it.
 *
 * The confidence half is resolved HERE rather than at the call site, and only
 * one resolution is correct: the server's score belongs to the pairing the
 * SERVER proposed. Choosing a different candidate off the list is a person
 * naming the pairing themselves, which the approve route treats as its own
 * thing (spec §3 — it is the only way a rejected pairing is ever revived), and
 * the matcher never scored it. Copying the proposal's confidence onto a
 * hand-picked candidate would put a number on the screen that was measured for
 * a different entry.
 */
export function ackKeyForChoice(line, journalLineId) {
  if (!line || !journalLineId) return null;
  const proposed = line.proposal?.journalLineId ?? null;
  const confidence = journalLineId === proposed ? line.proposal?.confidence ?? null : null;
  return matchAckKey(line.statementLineId, journalLineId, confidence);
}

// ===========================================================================
// The bands, and what they mean on screen
// ===========================================================================

/**
 * How the server's band word is presented — and NOTHING ELSE follows from it.
 *
 * Spec §1: `< 0.35` writes no proposal at all, `0.35–0.70` is `possible` and is
 * NOT pre-selected, `≥ 0.70` is `confident` and IS pre-selected. Those numbers
 * live on the server; this table is handed the word it chose.
 *
 * ⚠ PRE-SELECTION IS NOT APPROVAL, and the difference is the whole gate. A
 * pre-selected candidate is a radio button already filled in — it saves a
 * click and asserts nothing. The tick is separate, is keyed (above), and is
 * required for `confident` exactly as it is for everything else. Spec §1:
 * *"0.70 is a presentation threshold and nothing else… Nothing auto-approves,
 * full stop."*
 */
export const BAND = {
  confident: {
    label: 'Confident',
    // Fills the radio in. Does not tick anything, and cannot.
    preSelect: true,
    note: 'The strongest evidence the matcher found. It still needs your tick.',
  },
  possible: {
    label: 'Possible',
    preSelect: false,
    note: 'Enough to be worth showing you, not enough to fill in. Read the reason and choose.',
  },
};

export const bandPresentation = (band) => BAND[band] ?? null;

/**
 * The candidate a radio group starts on, or `null` for "nothing chosen".
 *
 * ONLY a `confident` proposal pre-selects. Everything else — `possible`, a tie,
 * a list of candidates below the threshold — starts empty, so that sending
 * anything at all requires a person to have pointed at something.
 */
export const preselectedJournalLineId = (line) => (
  line?.proposal && bandPresentation(line.proposal.band)?.preSelect
    ? line.proposal.journalLineId
    : null
);

/** What this line is currently paired with: what was picked, else the pre-selection. */
export const chosenJournalLineId = (line, picked = {}) => (
  picked[line?.statementLineId] ?? preselectedJournalLineId(line)
);

/**
 * Everything a bulk approve would actually send.
 *
 * DERIVED FROM THE ACKNOWLEDGEMENTS, NOT FROM A LIST OF ROWS, and that is the
 * whole defence. The set holds keys, so a row whose proposal changed under the
 * reviewer — a re-run that swapped the entry, or re-scored it — no longer has
 * a key in the set and is silently left out rather than silently sent. There
 * is nothing to reset and nothing to remember to reset.
 *
 * Returns the pairing as well as the line, because the approve route takes
 * `journalLineId` and the answer must be the one that was TICKED, never
 * re-derived from the line a second time.
 */
export function approvableChoices(lines = [], acked = new Set(), picked = {}) {
  const has = (k) => (acked instanceof Set ? acked.has(k) : Array.isArray(acked) && acked.includes(k));
  const out = [];
  for (const line of lines) {
    if (line?.outcome === 'already-decided') continue;
    const journalLineId = chosenJournalLineId(line, picked);
    if (!journalLineId) continue;
    const key = ackKeyForChoice(line, journalLineId);
    if (!has(key)) continue;
    out.push({ statementLineId: line.statementLineId, journalLineId, key });
  }
  return out;
}

// ===========================================================================
// The outcomes, honestly
// ===========================================================================

/**
 * The seven outcomes the matcher reports, each said as what it IS.
 *
 * ⚠ SIX OF THESE ARE "NO PROPOSAL" AND THEY ARE NOT THE SAME FACT. Collapsing
 * them into one grey "no match" is the failure the spec names twice: §6.2 —
 * *"a bare 'no match' sends the bookkeeper looking for a capture that is not
 * missing"* — and §6.4, where a consolidated fee row with nothing captured
 * against it is *"the common case in a first reconciliation, not an error
 * state, and the screen should read that way."*
 *
 * `tone` is presentational only and there is deliberately no 'error' tone
 * anywhere below. Nothing here is a failure: the matcher declining to guess is
 * the matcher working.
 *
 * `tied` in particular MUST NOT look like a breakage. Two candidates were
 * equally good and the matcher refused to flip a coin — spec §6.1: *"proposing
 * one of two genuinely different candidates is worse than proposing none: a
 * confident-looking badge gets ticked, and the wrong pairing satisfies every
 * invariant in this document, so nothing downstream can ever detect it."* That
 * is a matcher that has done its job well and handed the one decision it
 * cannot make to the one party who can.
 */
export const OUTCOME = {
  proposed: {
    label: 'Proposed',
    tone: 'proposal',
    note: 'The matcher paired this with one entry. Read why, then approve or reject it.',
  },
  tied: {
    label: 'Two entries fit equally',
    tone: 'choice',
    // Not "could not match". It could; it declined to guess between two.
    note: 'Two different entries fit this equally well, so the matcher did not choose between '
      + 'them. Pick the right one, or leave it for someone who knows.',
  },
  'below-threshold': {
    label: 'Too little to propose',
    tone: 'choice',
    note: 'Something in the ledger has this amount, but not enough else agrees for the matcher to '
      + 'put it forward. The candidates are listed; choose one only if you recognise it.',
  },
  blocked: {
    label: 'Already matched elsewhere',
    tone: 'settled',
    // A DECIDED claim. Undoing the other approval is the only way through.
    note: 'The entry that fits already carries an approved match to another statement line — '
      + 'usually the same movement appearing in two overlapping statements. Nothing is missing '
      + 'from the ledger.',
  },
  'claimed-in-run': {
    label: 'Claimed by another line here',
    tone: 'settled',
    // NOT the same as blocked: nobody has decided anything yet. Settle the
    // other line and this one may come back on the next run.
    note: 'The entry that fits is proposed for a different line of THIS statement, and one entry '
      + 'can only account for one line. Settle that line first and run the matcher again.',
  },
  'no-candidates': {
    label: 'Nothing in the ledger',
    tone: 'residual',
    // The common case in a first reconciliation, and the one that leads
    // somewhere: capture it, post the batch, run the matcher again, approve.
    note: 'Nothing captured has this amount within seven days of it. If the bank is right, this '
      + 'has not been captured yet — enter it in the cash book, post the batch, then run the '
      + 'matcher again.',
  },
  'already-decided': {
    label: 'Approved',
    tone: 'settled',
    note: 'A person has already approved a match for this line.',
  },
};

export const outcomeMeta = (outcome) => OUTCOME[outcome] ?? {
  label: outcome ?? 'Unknown',
  tone: 'choice',
  // An outcome this screen has never heard of is reported as exactly that,
  // rather than being folded into the nearest familiar one. A new outcome
  // added on the server must be visible as new.
  note: 'The matcher reported something this screen does not have wording for. Treat it as '
    + 'undecided and check the line by hand.',
};

/** Does this line still want a person? Everything but an approved one does. */
export const needsDecision = (line) => line?.outcome !== 'already-decided';

/**
 * The queue, as the screen lists it.
 *
 * 'todo' is the default for the same reason SlipReview's is: the queue is what
 * needs a human, and a statement is 274 rows of which most are settled after
 * the first pass. 'all' is the audit view.
 */
export const filterQueue = (lines = [], mode = 'todo') => (
  mode === 'all' ? [...lines] : lines.filter(needsDecision)
);

// ===========================================================================
// Money — the only arithmetic on this screen
// ===========================================================================

/**
 * Integer cents (as the string the wire carries) to the decimal string
 * `money()` formats. BigInt throughout: nothing in this browser turns money
 * into a JS number, and a bank statement is exactly where a float would first
 * be noticed and last be explained.
 */
export function centsToAmount(cents) {
  if (cents === null || cents === undefined || cents === '') return null;
  let n;
  try { n = BigInt(cents); } catch { return null; }
  const neg = n < 0n;
  const abs = neg ? -n : n;
  return `${neg ? '-' : ''}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}

/**
 * A statement line's amount split into the two money columns a ledger has.
 *
 * SIGN IS DIRECTION, and it is the server's sign, read and never recomputed:
 * the plan settled that money out is negative on the statement's `Amount`
 * column and on a journal line of the bank account alike, and the balance chain
 * is what proves it (spec §1 — *"if the sign convention were inverted the chain
 * would break on row 2"*). So this splits; it does not decide.
 *
 * Money INTO the bank is a debit of the bank account and money out is a credit,
 * which is why the columns take `.col-debit` and `.col-credit` and their
 * ledger-blue and oxblood rules. A paper cash book has always had two columns;
 * flattening both to one grey figure is what makes an amount's direction
 * something you have to read a minus sign to know.
 */
export function amountColumns(cents) {
  const amount = centsToAmount(cents);
  if (amount === null) return { moneyIn: null, moneyOut: null };
  const negative = amount.startsWith('-');
  return {
    moneyIn: negative ? null : amount,
    moneyOut: negative ? amount.slice(1) : null,
  };
}

/**
 * The server's confidence, for display beside the band word — never in the
 * basis sentence.
 *
 * Spec §2 is emphatic that `basis` never prints the score: *"a percentage next
 * to a sentence invites the reader to trust the number instead of reading the
 * sentence, which is the exact failure the sentence exists to prevent."* So the
 * figure is set apart from the sentence, in the monospace face every other
 * figure on this screen uses, and it is the server's own `numeric(4,3)` shown
 * to three decimals — copied, not computed, and not turned into a percentage,
 * because a percentage reads as a probability and this is a score.
 */
export const formatConfidence = (confidence) => (
  confidence === null || confidence === undefined || Number.isNaN(Number(confidence))
    ? null
    : Number(confidence).toFixed(3)
);

/**
 * The candidate row the proposal points at, so the proposal can be shown with
 * the evidence the candidate list already carries — the entry's own date,
 * description and day gap.
 *
 * The proposal object deliberately carries only ids, the score, the band and
 * `autoApply: false`; everything a person READS about the entry is on the
 * candidate. Looking it up here rather than in the component keeps the lookup
 * testable and stops two places disagreeing about which candidate is the
 * proposed one.
 */
export const proposedCandidate = (line) => (
  line?.proposal
    ? (line.candidates ?? []).find((c) => c.journalLineId === line.proposal.journalLineId) ?? null
    : null
);

/**
 * Is this candidate unavailable, and why?
 *
 * `claimedBy` comes off the matcher's own candidate list — blocked candidates
 * are included in it deliberately (spec §6.2) *"so the reason they are
 * unavailable is visible rather than inferred from an empty list"*. A radio
 * button for a candidate nobody can take must be disabled, or the screen offers
 * a choice the API will refuse with a 409.
 */
export const candidateUnavailable = (candidate) => Boolean(candidate?.claimedBy);
